/* Visual frame choices delegate to the existing native selector. No commerce state is reimplemented. */
(() => {
  if (customElements.get('galrie-frame-selector')) return;
  class GalrieFrameSelector extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.mediaSelectionRequested = new URL(window.location.href).searchParams.has('variant');
      this.main = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.native = this.main?.querySelector('variant-selects');
      this.selects = Array.from(this.native?.querySelectorAll('select') || []);
      this.frameIndex = this.selects.findIndex(s => s.name === `options[${this.dataset.optionName}]`);
      this.frame = this.selects[this.frameIndex];
      this.buttons = Array.from(this.querySelectorAll('button[data-frame-value]'));
      const raw = this.native?.querySelector('script[type="application/json"]');
      if (!this.frame || this.native.contains(this) || this.buttons.length !== 4 || this.frame.options.length !== this.buttons.length || !raw) return;
      try { this.variants = JSON.parse(raw.textContent).map(v => ({ id: v.id, media: v.featured_media?.id, options: [...v.options], available: v.available })); }
      catch { return; }
      if (!this.buttons.every(b => Array.from(this.frame.options).some(o => o.value === b.dataset.frameValue))) return;
      // Reorder only the existing Size option nodes; preserve exact values and selection.
      const size = this.selects.find(select => select.name === `options[${this.dataset.sizeOptionName}]`);
      if (size) {
        const selected = size.value;
        const order = ['A4 ', 'A3 ', 'A2 ', '50x70 ', 'A1 ', '70x100 ', 'A0 '];
        const rank = option => { const index = order.findIndex(prefix => option.value.startsWith(prefix)); return index < 0 ? order.length : index; };
        Array.from(size.options).sort((a,b) => rank(a) - rank(b)).forEach(option => size.append(option));
        size.value = selected;
      }
      const signal = this.abort.signal;
      this.addEventListener('click', e => {
        const button = e.target.closest('button[data-frame-value]');
        if (button && this.contains(button) && !button.disabled) this.choose(button);
      }, { signal });
      this.addEventListener('keydown', e => {
        if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(e.key)) return;
        const enabled = this.buttons.filter(b => !b.disabled);
        if (!enabled.length) return;
        const at = enabled.indexOf(e.target.closest('button'));
        let next = e.key === 'Home' ? 0 : e.key === 'End' ? enabled.length - 1 : (at + (['ArrowRight','ArrowDown'].includes(e.key) ? 1 : -1) + enabled.length) % enabled.length;
        e.preventDefault(); enabled[next].focus(); this.choose(enabled[next]);
      }, { signal });
      this.main.addEventListener('change', () => { this.mediaSelectionRequested = true; this.sync(); }, { signal });
      window.addEventListener('pageshow', () => this.sync(), { signal });
      window.addEventListener('popstate', () => { this.mediaSelectionRequested = true; this.sync(); }, { signal });
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.native, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      this.sync();
      this.retryTimers = [150, 600, 1500].map(delay => setTimeout(() => this.sync(), delay));
      this.hidden = false;
      this.frameGroup = this.frame.closest('.product-form__controls');
      this.frameGroup?.classList.add('galrie-native-frame-enhanced');
      this.main.classList.add('galrie-selection-enhanced');
    }
    choose(button) {
      this.mediaSelectionRequested = true;
      if (this.frame.value !== button.dataset.frameValue) {
        this.frame.value = button.dataset.frameValue;
        this.frame.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.sync();
    }
    sync() {
      if (!this.frame?.isConnected) return;
      const current = this.selects.map(s => s.value);
      const allowed = this.buttons.map(button => {
        const desired = [...current]; desired[this.frameIndex] = button.dataset.frameValue;
        const variant = this.variants.find(v => v.options.length === desired.length && v.options.every((value,i) => value === desired[i]));
        const nativeOption = Array.from(this.frame.options).find(o => o.value === button.dataset.frameValue);
        const available = Boolean(variant?.available) && Boolean(nativeOption) && !this.frame.disabled && !nativeOption.disabled;
        button.disabled = !available;
        button.setAttribute('aria-checked', String(button.dataset.frameValue === this.frame.value));
        const note = button.querySelector('[data-availability]');
        const status = available ? '' : (this.dataset.unavailableLabel || '');
        if (note.textContent !== status) note.textContent = status;
        return available;
      });
      let focusIndex = this.buttons.findIndex((b,i) => allowed[i] && b.dataset.frameValue === this.frame.value);
      if (focusIndex < 0) focusIndex = allowed.indexOf(true);
      this.buttons.forEach((b,i) => { b.tabIndex = i === focusIndex ? 0 : -1; });
      const selected = this.buttons.find(b => b.dataset.frameValue === this.frame.value);
      this.showFrameMedia(current, selected);
      const summary = this.querySelector('[data-selected-frame]');
      if (summary && summary.textContent !== selected?.dataset.label) summary.textContent = selected?.dataset.label || this.frame.value;
    }
    showFrameMedia(current, selected) {
      if (!this.mediaSelectionRequested) return;
      const variant = this.variants.find(v => v.options.every((value,i) => value === current[i]));
      if (!variant || !selected) return;
      // A variant photo reused by other finishes cannot describe the selected frame.
      const uniqueToFinish = variant.media && !this.variants.some(v => v.media === variant.media && v.options[this.frameIndex] !== current[this.frameIndex]);
      const mediaId = selected.dataset.frameRole === 'unframed' || uniqueToFinish ? (variant.media || selected.dataset.galleryMediaId) : selected.dataset.galleryMediaId;
      if (!mediaId) return;
      const key = `${variant.id}:${mediaId}`;
      if (this.mediaKey === key) return;
      const slider = this.main.querySelector('.js-media-list')?.swiper;
      if (!slider) return;
      const index = Array.from(slider.slides).findIndex(slide => slide.dataset.mediaId?.endsWith(`-${mediaId}`));
      if (index < 0) return;
      slider.slideTo(index);
      this.mediaKey = key;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect();
      this.retryTimers?.forEach(clearTimeout);
      this.frameGroup?.classList.remove('galrie-native-frame-enhanced');
      this.main?.classList.remove('galrie-selection-enhanced');
    }
  }
  customElements.define('galrie-frame-selector', GalrieFrameSelector);
  document.addEventListener('click', event => {
    const link = event.target.closest('.galrie-about-link');
    if (!link) return;
    const details = document.getElementById(link.hash.slice(1));
    if (details?.tagName === 'DETAILS') details.open = true;
  });
})();


